import { FC, useRef } from "react";
import "./JointAccountOwners.scss";
import {
  CoreFieldValue,
  FieldFormV2Module,
  LoadingV2,
  SecondaryButton,
  SecondaryCard,
  SecondaryCardHeader,
} from "@ucl/library";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { PersonInformation } from "@/types";
import {
  getFullName,
} from "../ComplexFields/PersonInformationComplexField";
import { JointAccountOwner } from "@/types/JointAccountOwner";
import { useJointAccountOwner } from "@/hooks/useJointAccountOwner";
import { FinancialApplicationFormSectionContextType } from "@/hooks/useFinancialApplicationFormSectionValidation";
import { JointAccountOwnerForm } from "./JointAccountOwnerForm";

export interface JointAccountOwnerItemProps {
  financialApplicationId: string;
  jointOwnerId: string;
  onDelete: (jointOwnerId: string) => void;
  validationErrors: Record<string, string[]>;
  onBlur?: (field: CoreFieldValue) => void;
  onSecurityQuestionsBlur?: (field: CoreFieldValue) => void;
  isInitiallyCollapsed?: boolean;
  allowChangingWhetherCollapsed?: boolean;
  readOnly?: boolean;
  validationContextType: FinancialApplicationFormSectionContextType;
  shouldHideSecurityQuestions?: boolean;
  shouldAskIfCanProvideGovernmentIdInformation?: boolean;
  shouldAlwaysShowGovernmentIdInformationFields?: boolean;
  shouldShowDigitalBankingFields?: boolean;
  shouldShowDebitCardFields?: boolean;
}

export const JointAccountOwnerItem: FC<JointAccountOwnerItemProps> = (props) => {
  const fieldFormModule = useRef<FieldFormV2Module<JointAccountOwner> | null>(null);

  const {
    formValue,
    setFormValue,
    saveFormData,
    debitCardInformationRenderKey,
    digitalBankingInformationRenderKey,
  } = useJointAccountOwner(props.financialApplicationId, props.jointOwnerId);

  if (!formValue) {
    return (
      <section className="joint-account-owner-form">
        <LoadingV2 />
      </section>
    );
  }

  return (
    <section className="joint-account-owner-form">
      <SecondaryCard
        hideBody={props.isInitiallyCollapsed ?? false}
        isCollapsible={props.allowChangingWhetherCollapsed ?? true}
        headerContent={
          <>
            <div className="joint-account-owner-form__header">
              <SecondaryCardHeader
                label={
                  getFullName(formValue?.personInformation as PersonInformation) ||
                  "New Joint Account Owner"
                }
              />
              {props.validationErrors && Object.keys(props.validationErrors).length > 0 && (
                <span className="joint-account-owner-form__validation-errors e-badge e-badge-pill">
                  {Object.keys(props.validationErrors).length}
                </span>
              )}
            </div>
            {!props.readOnly && (
              <SecondaryButton
                minimal={true}
                iconProps={{ icon: faTrash }}
                cssClass="subtle danger-button"
                onClick={() => {
                  props.onDelete(props.jointOwnerId);
                }}
                disabled={props.readOnly}
              />
            )}
          </>
        }
      >
        <JointAccountOwnerForm
          fieldFormModule={fieldFormModule}
          formValue={formValue}
          setFormValue={setFormValue}
          saveFormData={saveFormData}
          debitCardInformationRenderKey={debitCardInformationRenderKey}
          digitalBankingInformationRenderKey={digitalBankingInformationRenderKey}
          validationErrors={props.validationErrors}
          onBlur={props.onBlur}
          onSecurityQuestionsBlur={props.onSecurityQuestionsBlur}
          validationContextType={props.validationContextType}
          shouldHideSecurityQuestions={props.shouldHideSecurityQuestions}
          shouldAskIfCanProvideGovernmentIdInformation={props.shouldAskIfCanProvideGovernmentIdInformation}
          shouldAlwaysShowGovernmentIdInformationFields={props.shouldAlwaysShowGovernmentIdInformationFields}
          shouldShowDigitalBankingFields={props.shouldShowDigitalBankingFields}
          shouldShowDebitCardFields={props.shouldShowDebitCardFields}
        />
      </SecondaryCard>
    </section>
  );
};
